import React from 'react';
import { Box, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { REPORT_RIGHTS } from './reportRights';

// ─── Constants ──────────────────────────────────────────────────────
export const REQUEST_TYPES = REPORT_RIGHTS.map(right => ({
    ...right,
    label: right.key === 'rtk' ? 'Right to Know' : right.key === 'rtoo' ? 'Right to Opt-Out' : right.label,
}));

// Parse "11+ Days (02/12 & earlier)" → { title: "11+ Days", subtitle: "02/12 & earlier" }
const parseAgeDateLabel = (label) => {
    const match = label?.match(/^(.+?)\s*\((.+)\)$/);
    if (match) {
        return { title: match[1].trim(), subtitle: match[2].trim() };
    }
    return { title: label || '', subtitle: '' };
};

// ─── Styles ─────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        padding: '8px 0',
        '@media (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
        '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
        },
    },

    card: {
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e0e7ef',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        },
    },
    cardHeader: {
        padding: '12px 16px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column',
    },
    headerTitle: {
        fontSize: '1.05rem',
        fontWeight: 700,
        lineHeight: 1.3,
    },
    headerSubtitle: {
        fontSize: '0.72rem',
        fontWeight: 400,
        opacity: 0.85,
        marginTop: '2px',
    },
    headerRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    totalLabel: {
        fontSize: '0.68rem',
        fontWeight: 500,
        opacity: 0.8,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    totalValue: {
        fontSize: '1.2rem',
        fontWeight: 800,
        lineHeight: 1.2,
    },
    cardBody: {
        padding: '12px 16px',
    },
    chipRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
        fontSize: '0.8rem',
        borderBottom: '1px solid #f0f2f5',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    chipLabel: {
        color: '#555',
        fontWeight: 500,
        flex: 1,
    },
    chipValue: {
        fontWeight: 700,
        minWidth: '36px',
        textAlign: 'right',
    },
}));

const CARD_COLORS = ['#1a3a5c', '#267db7', '#3498db', '#5dade2'];

// ─── Shared Card Header Component ───────────────────────────────────
const CardHeader = ({ title, subtitle, total, backgroundColor, classes }) => (
    <Box className={classes.cardHeader} sx={{ background: backgroundColor }}>
        <Box className={classes.headerLeft}>
            <span className={classes.headerTitle}>{title}</span>
            {subtitle && <span className={classes.headerSubtitle}>{subtitle}</span>}
        </Box>
        <Box className={classes.headerRight}>
            <span className={classes.totalLabel}>Total</span>
            <span className={classes.totalValue}>{total}</span>
        </Box>
    </Box>
);

// ─── AGING REPORT CARD LAYOUT ───────────────────────────────────────
export const CardLayoutView = ({ agingRows, ageDateLabels, onCellClick, requestTypes = REQUEST_TYPES }) => {
    const classes = useStyles();

    return (
        <Box className={classes.cardGrid}>
            {agingRows.map((row, idx) => {
                const total = requestTypes.reduce((sum, rt) => sum + (row[rt.key] || 0), 0);
                const { title, subtitle } = parseAgeDateLabel(ageDateLabels[idx]);
                return (
                    <Paper key={row.id} className={classes.card} elevation={0}>
                        <CardHeader
                            title={title}
                            subtitle={subtitle}
                            total={total}
                            backgroundColor={CARD_COLORS[idx]}
                            classes={classes}
                        />
                        <Box className={classes.cardBody}>
                            {requestTypes.map((rt) => {
                                const val = row[rt.key] || 0;
                                return (
                                    <Box key={rt.key} className={classes.chipRow}>
                                        <span className={classes.chipLabel}>{rt.label}</span>
                                        <span
                                            className={classes.chipValue}
                                            style={{
                                                color: val > 0 ? '#267db7' : '#bbb',
                                                cursor: val > 0 ? 'pointer' : 'default',
                                                textDecoration: val > 0 ? 'underline' : 'none',
                                            }}
                                            onClick={() => {
                                                if (val > 0) {
                                                    onCellClick(idx, rt.columnMappingIndex);
                                                }
                                            }}
                                        >
                                            {val}
                                        </span>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                );
            })}
        </Box>
    );
};

